import { useCallback, useState } from 'react';

export type WikiEditorMode = 'source' | 'split' | 'preview';

const STORAGE_KEY = 'bb_wiki_editor_mode';

function isMode(value: unknown): value is WikiEditorMode {
  return value === 'source' || value === 'split' || value === 'preview';
}

function readStored(): WikiEditorMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return isMode(stored) ? stored : 'source';
  } catch (err) {
    // Private browsing can throw on any storage access; plain source editing is the safe default.
    return 'source';
  }
}

/** Source / split / preview layout of the wiki editor, remembered across sessions. */
export function useWikiEditorMode(): [WikiEditorMode, (mode: WikiEditorMode) => void] {
  const [mode, setMode] = useState<WikiEditorMode>(readStored);
  const update = useCallback((next: WikiEditorMode) => {
    setMode(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch (err) {
      // The mode still applies for this session; only persistence is lost.
      console.warn('Could not save the wiki editor mode.', err);
    }
  }, []);
  return [mode, update];
}
